// Payments page (M5). Loads the payment list once, then keeps it fresh off the
// payment.changed SSE — mirroring ClinicsPage's load / live-refresh / queued-mutate shape.
// "Record payment" opens a modal that POSTs through the offline queue; a per-row Verify
// action posts the pending → verified transition (both reconcile on the SSE refresh).
import { useCallback, useEffect, useState } from 'react';
import {
  type PaymentDto,
  type PaymentMethod,
  type PaymentStatus,
  addPaymentSchema,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  formatDate,
  formatMoney,
} from '@alignova/shared';
import { api, isDeferred } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button, Card, PageHeader, Pill } from '../../ui/primitives.tsx';

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm';

type Tone = 'neutral' | 'good' | 'bad';

function statusTone(status: PaymentStatus): Tone {
  if (status === 'verified') return 'good';
  if (status === 'void') return 'bad';
  return 'neutral';
}

function statusLabel(status: PaymentStatus): string {
  return t(`finance.payments.status.${status}`);
}

export function PaymentsPage() {
  const can = useCan();
  const manage = can('invoice:manage');
  const canVerify = can('finance:verify_payment');
  const [payments, setPayments] = useState<PaymentDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    api
      .get<PaymentDto[]>('/finance/payments')
      .then(setPayments)
      .catch(() => setError(t('finance.payments.loadError')));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A payment was recorded or verified somewhere (our own queued replay or another user's
  // edit) — pull fresh truth so every open tab reconciles.
  useLiveEvent('payment.changed', () => {
    setQueued(false);
    load();
  });

  async function verify(payment: PaymentDto) {
    setError(null);
    try {
      const res = await api.post<PaymentDto>(
        `/finance/payments/${payment.id}/verify`,
        {},
        { queue: true },
      );
      if (isDeferred(res)) setQueued(true);
    } catch {
      setError(t('finance.payments.verifyError'));
      load();
    }
  }

  const showActions = canVerify;

  return (
    <div>
      <PageHeader title={t('finance.payments.title')} subtitle={t('finance.payments.subtitle')} />

      <div className="mb-4 flex items-center gap-2">
        {manage && (
          <Button className="ml-auto" onClick={() => setShowModal(true)}>
            {t('finance.payments.record')}
          </Button>
        )}
      </div>

      {queued && (
        <div className="mb-3 rounded border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
          {t('sync.queued')}
        </div>
      )}
      {error && <p className="mb-3 text-xs text-status-urgent-fg">{error}</p>}

      <Card className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-3 font-medium">{t('finance.payments.col.date')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.payments.col.client')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.payments.col.invoice')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.payments.col.amount')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.payments.col.method')}</th>
              <th className="px-4 py-3 font-medium">{t('finance.payments.col.status')}</th>
              {showActions && (
                <th className="px-4 py-3 font-medium">{t('finance.payments.col.actions')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && (
              <tr>
                <td colSpan={showActions ? 7 : 6} className="px-4 py-6 text-center text-muted">
                  {t('finance.payments.empty')}
                </td>
              </tr>
            )}
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-line hover:bg-surface-2">
                <td className="px-4 py-2 font-mono text-xs">{formatDate(p.paymentDate)}</td>
                <td className="px-4 py-2">{p.clientId ?? '-'}</td>
                <td className="px-4 py-2 font-mono text-xs">{p.invoiceId ?? '-'}</td>
                <td className="px-4 py-2 font-mono text-xs">{formatMoney(p.amount)}</td>
                <td className="px-4 py-2">{PAYMENT_METHOD_LABELS[p.method]}</td>
                <td className="px-4 py-2">
                  <Pill tone={statusTone(p.status)}>{statusLabel(p.status)}</Pill>
                </td>
                {showActions && (
                  <td className="px-4 py-2">
                    {canVerify && p.status === 'pending' && (
                      <Button variant="ghost" onClick={() => verify(p)}>
                        {t('finance.payments.verify')}
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <PaymentModal open={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}

function PaymentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [invoiceId, setInvoiceId] = useState('');
  const [clientId, setClientId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [reference, setReference] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInvoiceId('');
    setClientId('');
    setAmount('');
    setMethod('cash');
    setReference('');
    setPaymentDate('');
    setNotes('');
    setError(null);
    setSubmitting(false);
  }, [open]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = addPaymentSchema.safeParse({
      invoiceId: invoiceId.trim() || undefined,
      clientId: clientId.trim() || undefined,
      amount: Math.round(Number(amount) * 100),
      method,
      reference: reference.trim() || undefined,
      paymentDate: paymentDate.trim() || undefined,
      notes: notes.trim() || undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('finance.payments.form.invalid'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post<PaymentDto>('/finance/payments', parsed.data, { queue: true });
      onClose();
    } catch {
      setError(t('finance.payments.form.saveError'));
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
      <Card className="max-h-screen w-full max-w-lg overflow-y-auto p-6">
        <h2 className="mb-4 text-lg font-semibold text-ink">{t('finance.payments.form.title')}</h2>
        <form className="space-y-4" onSubmit={submit}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('finance.payments.form.invoiceId')}
              </label>
              <input
                className={inputClass}
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
              />
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('finance.payments.form.clientId')}
              </label>
              <input
                className={inputClass}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('finance.payments.form.amount')}
              </label>
              <input
                className={inputClass}
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('finance.payments.form.method')}
              </label>
              <select
                className={inputClass}
                value={method}
                onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {PAYMENT_METHOD_LABELS[m]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('finance.payments.form.reference')}
              </label>
              <input
                className={inputClass}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('finance.payments.form.paymentDate')}
              </label>
              <input
                className={inputClass}
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
          </div>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
              {t('finance.payments.form.notes')}
            </label>
            <textarea
              className={inputClass}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('finance.payments.form.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('finance.payments.form.saving') : t('finance.payments.form.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
